// ThreatsTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomBox from "../../../../../../Components/UI/CustomBox/CustomBox/CustomBox";
import { useOnClickOutside } from "../../../../../../utils/hook/useOnClickOutside";
import ArrowBottomIcon from "../../../../../../Components/Icon-SVG/ArrowBottomIcon";

import "./styles.scss";

const CustomSelect = ({
  data,
  width = "80px",
  height = "32px",
  color = "var(--dashboard-value-color)",
  setSelected,
  select,
  placeholder,
  onSelectItem = null,
}) => {
  const [showItems, setShowItems] = useState(false);

  const ref = useRef();

  useOnClickOutside(ref, () => setShowItems(false));

  const dropDown = () => {
    setShowItems(!showItems);
  };

  const selectItem = (item) => {
    setShowItems(false);
    setSelected(item);
  };

  return (
    <div className="select-box--box" style={{ width }} ref={ref}>
      <div className="select-box--container" onClick={dropDown} style={{ width, color, height }}>
        <div className="select-box--selected-item">
          {select || select === 0 ? (
            select
          ) : (
            <span className="placeholder">{placeholder && placeholder}</span>
          )}
        </div>
        <div className="select-box--arrow">
          <ArrowBottomIcon width={14} height={14} />
        </div>
      </div>

      <div
        className="select-box--options"
        style={{
          display: showItems ? "block" : "none",
          width,
        }}>
        {data.map((item) => (
          <div
            key={item}
            onClick={() => selectItem(item)}
            className={`select ${select === item ? "selected" : ""}`}>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

function weightToNumber(w) {
  if (w === undefined || w === null) return 0;
  const n = typeof w === "string" ? parseFloat(w) : w;
  return Number.isFinite(n) ? n : 0;
}
function weightChipColor(severity) {
  if (severity === undefined || severity === null || typeof severity !== "string") {
    return "#757575";
  }
  if (severity === "critical" || severity === "high") return "#d32f2f";
  if (severity === "medium") return "#ffb300";
  if (severity === "green") return "#388e3c";
  return "#757575";
}

export default function ThreatsTable({ threats = [] }) {
  const [query, setQuery] = useState("");
  const [minWeight, setMinWeight] = useState(0);
  const [sortBy, setSortBy] = useState("weight");

  const [visibleCount, setVisibleCount] = useState(50);
  const contentRef = useRef(null);
  const sentinelRef = useRef(null);

  const filtered = useMemo(() => {
    let list = threats.map((t) => ({ ...t, _w: weightToNumber(t.weight) }));

    if (minWeight > 0) list = list.filter((t) => t._w >= minWeight);

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (t) =>
          (t.threat || "").toLowerCase().includes(q) || (t.details || "").toLowerCase().includes(q),
      );
    }

    list.sort((a, b) =>
      sortBy === "weight"
        ? b._w - a._w || String(a.threat).localeCompare(String(b.threat))
        : String(a.threat).localeCompare(String(b.threat)) || b._w - a._w,
    );
    return list;
  }, [threats, query, minWeight, sortBy]);

  useEffect(() => {
    setVisibleCount(50);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [query, minWeight, sortBy]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          setVisibleCount((n) => Math.min(n + 50, filtered.length));
        }
      },
      { root: contentRef.current, rootMargin: "0px", threshold: 1.0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filtered.length]);

  const rows = filtered.slice(0, visibleCount);

  if (!threats || threats.length === 0) {
    return (
      <CustomBox className="thunter-threats">
        <h2 className="thunter-threats__title">Threats</h2>
        <p className="thunter-threats__empty">No threats detected</p>
      </CustomBox>
    );
  }

  return (
    <CustomBox className="thunter-threats thunter-threats--fill">
      {/* Header */}
      <div className="thunter-threats__header">
        <h2 className="thunter-threats__title">Threats</h2>
        <span className="thunter-threats__count">
          {filtered.length} result{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Middle filters area */}
      <div className="thunter-threats__filters">
        <input
          className="settings__field"
          placeholder="Search threat or details…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="thunter-threats__control">
          <span>Min weight</span>
          {/* <select
            className="thunter-threats__select"
            value={minWeight}
            onChange={(e) => setMinWeight(Number(e.target.value))}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select> */}
          <CustomSelect
            // placeholder={"}
            // width={""}
            data={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            setSelected={setMinWeight}
            select={minWeight}
            height="40px"
          />
        </label>
        <label className="thunter-threats__control">
          <span>Sort by</span>
          {/* <select
            className="thunter-threats__select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}>
            <option value="weight">By weight</option>
            <option value="threat">By name</option>
          </select> */}
          <CustomSelect
            // placeholder={"}
            // width={""}
            data={["weight", "threat"]}
            setSelected={setSortBy}
            select={sortBy}
            height="40px"
          />
        </label>

        {(query || minWeight || sortBy !== "weight") && (
          <button
            className="default-button"
            onClick={() => {
              setQuery("");
              setMinWeight(0);
              setSortBy("weight");
            }}
            title="Clear filters">
            Reset
          </button>
        )}
      </div>

      <div className="thunter-threats__table">
        <div className="thunter-threats__head">
          <div className="thunter-threats__cell thunter-threats__cell--weight">Weight</div>
          <div className="thunter-threats__cell thunter-threats__cell--threat">Threat</div>
          <div className="thunter-threats__cell thunter-threats__cell--details">Details</div>
        </div>

        <div className="thunter-threats__content" ref={contentRef}>
          {rows.length ? (
            rows.map((t, i) => {
              const w = weightToNumber(t.weight);
              const color = weightChipColor(t.severity);
              return (
                <div key={`${t.threat}-${i}`} className="thunter-threats__row">
                  <div className="thunter-threats__cell thunter-threats__cell--weight">
                    <span
                      className="thunter-threats__weight-chip"
                      style={{ backgroundColor: color }}
                      title={`Weight: ${w}`}>
                      {w}
                    </span>
                  </div>
                  <div
                    className="thunter-threats__cell thunter-threats__cell--threat"
                    title={t.threat}>
                    <code className="thunter-threats__mono">{t.threat}</code>
                  </div>
                  <div
                    className="thunter-threats__cell thunter-threats__cell--details"
                    title={t.details}>
                    {t.details || "—"}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="thunter-threats__row thunter-threats__row--empty">
              <div className="thunter-threats__empty">No threats match current filters.</div>
            </div>
          )}

          <div ref={sentinelRef} className="thunter-threats__sentinel" aria-hidden="true" />
        </div>
      </div>
    </CustomBox>
  );
}
